import { useCallback, useEffect, useMemo, useState } from "react"
import type { Location, To } from "react-router-dom"

import {
  createActionBarSuffix,
  createActionBarTitle,
  type SelectRouteAddressType,
} from "./select-route-ui"

export type { SelectRouteAddressType } from "./select-route-ui"

export interface SelectRouteMapVisibilityHandlers {
  readonly isMapVisible: boolean
  /** AppView always provides this; Zalo may omit when inline map toggle is unused. */
  setMapVisible?: (setter: (prev: boolean) => boolean) => void
  hideMap: () => void
  onBottomSheetClose: () => void
  onBottomSheetOpen: () => void
}

export interface UseSelectRouteLogicProps {
  /** App routers may accept `To | number`; this flow only navigates with a string path. */
  navigate: (
    to: To | number,
    options?: { state?: { addressType?: SelectRouteAddressType } }
  ) => void
  location: Location
  mapVisibilityHandlers: SelectRouteMapVisibilityHandlers
  initialAddressType?: SelectRouteAddressType
  /** Zalo: hide keyboard when the map sheet becomes visible */
  onMapBecameVisible?: () => void
  /** Zalo: report toggle/navigation failures (e.g. Sentry); AppView typically omits */
  onToggleMapError?: (error: unknown) => void
}

export interface UseSelectRouteLogicReturn {
  currentAddressType: SelectRouteAddressType
  setCurrentAddressType: (type: SelectRouteAddressType) => void
  handleToggleMap: () => void
  actionBarSuffix: { text: string; onClick: () => void } | null
  actionBarTitle: string
}

/**
 * Select-route page logic shared by AppView and Zalo; platform hooks inject Zalo-only behavior.
 */
export function useSelectRouteLogic({
  navigate,
  mapVisibilityHandlers,
  initialAddressType = "drop-off",
  onMapBecameVisible,
  onToggleMapError,
}: UseSelectRouteLogicProps): UseSelectRouteLogicReturn {
  const [currentAddressType, setCurrentAddressType] =
    useState<SelectRouteAddressType>(initialAddressType)

  useEffect(() => {
    if (mapVisibilityHandlers.isMapVisible) {
      onMapBecameVisible?.()
    }
  }, [mapVisibilityHandlers.isMapVisible, onMapBecameVisible])

  const handleToggleMap = useCallback(() => {
    try {
      if (
        currentAddressType !== "pick-up" &&
        currentAddressType !== "drop-off"
      ) {
        mapVisibilityHandlers.setMapVisible?.((currentState) => !currentState)
        return
      }

      navigate("/ride/dropoff_location/map", {
        state: {
          addressType: currentAddressType,
        },
      })
    } catch (error) {
      console.error("Failed to toggle map:", error)
      onToggleMapError?.(error)
    }
  }, [currentAddressType, navigate, mapVisibilityHandlers, onToggleMapError])

  const actionBarSuffix = useMemo(() => {
    return createActionBarSuffix(
      mapVisibilityHandlers.isMapVisible,
      handleToggleMap
    )
  }, [mapVisibilityHandlers.isMapVisible, handleToggleMap])

  const actionBarTitle = useMemo(() => {
    return createActionBarTitle(currentAddressType)
  }, [currentAddressType])

  return {
    currentAddressType,
    setCurrentAddressType,
    handleToggleMap,
    actionBarSuffix,
    actionBarTitle,
  }
}

/** @deprecated Use SelectRouteMapVisibilityHandlers */
export type MapVisibilityHandlers = SelectRouteMapVisibilityHandlers

/** @deprecated Use SelectRouteAddressType */
export type AddressType = SelectRouteAddressType
